import React, { useMemo, useState } from "react";
import {
  ShoppingCart,
  Package,
  CheckCircle,
  Settings,
  Maximize2,
  Minimize2,
  Search,
  X,
  Calendar,
  User,
} from "lucide-react";
import { getItemCountsForRequest } from "./sampleOrderItemUtils";
import { getSampleOrderStatusLabel } from "../../utils/sampleOrderStatusLabels";

/**
 * Canonical sample order identity fields (companies/{companyId}/sampleOrders/{orderId}):
 * - createdBy (uid)
 * - createdByName
 * - createdByRole (lowercase string: "customer" | "owner" | "admin" | "enduser")
 */

// -----------------------------
// Helpers
// -----------------------------
const normStr = (v) => (v == null ? "" : String(v)).trim();

const normalizeRole = (role) =>
  normStr(role).toLowerCase().replace(/[_\s-]+/g, "");

const isOwnerOrAdminRole = (role) => {
  const r = normalizeRole(role);
  return r === "owner" || r === "admin";
};

const isCustomerRole = (role) => normalizeRole(role) === "customer";

const normId = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    return v.id || v.uid || v.userId || v.value || null;
  }
  return String(v);
};

// Reuse the same date formatting style as the list view
const formatDeliveryDate = (order) => {
  const raw = order.deliveryDate || order.requestedDate;
  if (!raw) return null;

  let dateObj = null;
  if (raw && typeof raw.toDate === "function") {
    dateObj = raw.toDate();
  } else {
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) dateObj = parsed;
  }

  if (!dateObj) return null;
  return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ✅ Always produce a real-ish item name
const pickBestItemName = (item, request, idx, nameIndex) => {
  const tryStrings = [
    item?.productName,
    item?.itemName,
    item?.name,
    item?.title,
    item?.inventoryName,
    item?.label,
    item?.skuLabel,
    item?.productCode,
    item?.sku,
  ]
    .map((v) => (v || "").toString().trim())
    .filter(Boolean);

  if (tryStrings.length) return tryStrings[0];

  const keys = [
    item?.productId,
    item?.itemId,
    item?.inventoryId,
    item?.sku,
    item?.productCode,
  ]
    .map((v) => (v || "").toString().trim())
    .filter(Boolean);

  for (const k of keys) {
    if (nameIndex?.has(k)) return nameIndex.get(k);
  }

  const reqFallback = [request?.productName, request?.productCode, request?.productId]
    .map((v) => (v || "").toString().trim())
    .filter(Boolean);

  if (reqFallback.length) return reqFallback[0];

  return `Item ${idx + 1}`;
};

const buildNameIndexFromRequestItems = (requestItems = []) => {
  const idx = new Map();

  requestItems.forEach((it) => {
    const name = [
      it?.productName,
      it?.itemName,
      it?.name,
      it?.title,
      it?.inventoryName,
      it?.label,
      it?.skuLabel,
    ]
      .map((v) => (v || "").toString().trim())
      .filter(Boolean)[0];

    if (!name) return;

    const keys = [it?.productId, it?.itemId, it?.inventoryId, it?.sku, it?.productCode]
      .map((v) => (v || "").toString().trim())
      .filter(Boolean);

    keys.forEach((k) => {
      if (!idx.has(k)) idx.set(k, name);
    });
  });

  return idx;
};

// -----------------------------
// Component
// -----------------------------
const SampleOrderKanban = ({
  requests = [],
  isAdmin,
  currentUserId,
  currentUserRole = "",
  currentUserAccountId = null, // optional for customers
  onUpdateRequest,
  onSelectRequest,
  sampleStatusLabelSettings = {},
}) => {
  const [convertOrderId, setConvertOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [compactView, setCompactView] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showLaneConfig, setShowLaneConfig] = useState(false);

  const roleLower = normalizeRole(currentUserRole);
  const isAdminOrOwner = !!isAdmin || isOwnerOrAdminRole(roleLower);

  // ✅ Canonical visibility (NO requestedBy, NO legacy fallbacks):
  // - Admin/Owner: see all (except hidden)
  // - Non-admin staff: ONLY see "mine" (createdBy OR assignedToId/assignedTo)
  // - Customer: account match OR createdBy
  const visibleRequests = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];
    const me = normId(currentUserId);

    const notHidden = (o) => !o?.hidden;

    if (isAdminOrOwner) return arr.filter(notHidden);

    if (!me) return [];

    const getCreatedBy = (o) => normId(o?.createdBy);

    const getAssignedToId = (o) => {
      const v = o?.assignedToId ?? o?.assignedTo ?? null;
      return normId(v);
    };

    // Customer rules (optional): account match OR createdBy==me
    if (isCustomerRole(roleLower)) {
      const acct = normId(currentUserAccountId);
      return arr.filter((o) => {
        if (!notHidden(o)) return false;
        const createdBy = getCreatedBy(o);
        const orderAccountId = normId(o?.accountId);
        return (acct && orderAccountId && orderAccountId === acct) || createdBy === me;
      });
    }

    // Staff endUser rules: mine only (createdBy OR assignedTo)
    return arr.filter((o) => {
      if (!notHidden(o)) return false;
      const createdBy = getCreatedBy(o);
      const assignedToId = getAssignedToId(o);
      return createdBy === me || assignedToId === me;
    });
  }, [requests, isAdminOrOwner, currentUserId, roleLower, currentUserAccountId]);

  const STATUS_OPTIONS = [
    { id: "Pending", color: "bg-amber-50 border-amber-200 text-amber-700" },
    { id: "Approved", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    { id: "Preparation", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    { id: "Shipped", color: "bg-sky-50 border-sky-200 text-sky-700" },
    { id: "Delivered", color: "bg-blue-50 border-blue-200 text-blue-700" },
    { id: "Follow Up", color: "bg-rose-50 border-rose-200 text-rose-700" },
    { id: "Converted to Order", color: "bg-purple-50 border-purple-200 text-purple-700" },
    { id: "Completed", color: "bg-teal-50 border-teal-200 text-teal-700" },
    { id: "Cancelled", color: "bg-gray-50 border-gray-300 text-gray-600" },
  ].map((lane) => ({
    ...lane,
    title: getSampleOrderStatusLabel(lane.id, sampleStatusLabelSettings),
  }));

  const [visibleLanes, setVisibleLanes] = useState([
    "Pending",
    "Approved",
    "Delivered",
    "Follow Up",
    "Completed",
  ]);

  const handleDragStart = (e, requestId) => {
    if (!isAdminOrOwner) return;
    e.dataTransfer.setData("requestId", requestId);
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    if (!isAdminOrOwner) return;
    e.currentTarget.style.opacity = "1";
  };

  const handleDragOver = (e) => {
    if (!isAdminOrOwner) return;
    e.preventDefault();
    e.currentTarget.classList.add("ring-2", "ring-blue-400");
  };

  const handleDragLeave = (e) => {
    if (!isAdminOrOwner) return;
    e.currentTarget.classList.remove("ring-2", "ring-blue-400");
  };

  const handleDrop = (e, newStatus) => {
    if (!isAdminOrOwner) return;
    e.preventDefault();
    e.currentTarget.classList.remove("ring-2", "ring-blue-400");
    const requestId = e.dataTransfer.getData("requestId");
    if (!requestId) return;

    const request = visibleRequests.find((entry) => String(entry?.id) === String(requestId));
    const effectiveAssignedToId = normId(request?.assignedToId ?? request?.assignedTo ?? request?.assignedUserId ?? null);
    const requiresAssigneeAfterApproval = [
      "Approved",
      "Preparation",
      "Shipped",
      "Delivered",
      "Follow Up",
      "Completed",
      "Converted to Order",
    ].includes(newStatus);

    if (requiresAssigneeAfterApproval && !effectiveAssignedToId) {
      alert("Assign a representative before moving a sample request into or beyond Approved.");
      return;
    }

    onUpdateRequest && onUpdateRequest(requestId, { status: newStatus });
  };

  const handleConvertToOrder = (requestId) => {
    if (!isAdminOrOwner) return;

    if (!orderNumber.trim()) {
      alert("Please enter an order number");
      return;
    }

    onUpdateRequest &&
      onUpdateRequest(requestId, {
        convertedToOrder: true,
        orderNumber: orderNumber.trim(),
        status: "Converted to Order",
      });

    setConvertOrderId(null);
    setOrderNumber("");
  };

  const toggleLane = (laneId) => {
    setVisibleLanes((prev) =>
      prev.includes(laneId) ? prev.filter((id) => id !== laneId) : [...prev, laneId]
    );
  };

  const getRequestsByStatus = (status) => {
    return visibleRequests.filter((r) => {
      const matchesStatus = r.status === status;

      const q = (searchTerm || "").toLowerCase().trim();
      const matchesSearch =
        !q ||
        r.productName?.toLowerCase().includes(q) ||
        r.accountName?.toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });
  };

  const activeLanes = STATUS_OPTIONS.filter((lane) => visibleLanes.includes(lane.id));
  const totalRequests = visibleRequests.length;
  const allLanesVisible = visibleLanes.length === STATUS_OPTIONS.length;

  return (
    <div className="sample-order-kanban-shell space-y-3">
      {/* Header Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Sample Orders</h2>
            <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
              {totalRequests} Total
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search samples..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Compact View Toggle */}
            <button
              onClick={() => setCompactView(!compactView)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                compactView
                  ? "bg-blue-50 border-blue-200 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              title={compactView ? "Detailed View" : "Compact View"}
            >
              {compactView ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>

            <label className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={allLanesVisible}
                onChange={(e) => setVisibleLanes(e.target.checked ? STATUS_OPTIONS.map((lane) => lane.id) : ["Pending", "Approved", "Delivered", "Follow Up", "Completed"])}
                className="rounded border-gray-300"
              />
              Show all
            </label>

            {/* Lane Config */}
            <button
              onClick={() => setShowLaneConfig(!showLaneConfig)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Lanes
            </button>
          </div>
        </div>

        {/* Lane Configuration Panel */}
        {showLaneConfig && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">Select visible swim lanes:</p>
              <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
                <input
                  type="checkbox"
                  checked={allLanesVisible}
                  onChange={(e) => setVisibleLanes(e.target.checked ? STATUS_OPTIONS.map((lane) => lane.id) : ["Pending", "Approved", "Delivered", "Follow Up", "Completed"])}
                  className="rounded border-gray-300"
                />
                Show all
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((lane) => (
                <button
                  key={lane.id}
                  onClick={() => toggleLane(lane.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                    visibleLanes.includes(lane.id)
                      ? lane.color
                      : "bg-white border-gray-200 text-gray-400"
                  }`}
                >
                  {lane.title}
                  {visibleLanes.includes(lane.id) && (
                    <CheckCircle className="w-3.5 h-3.5 inline ml-1.5" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <div
        className={`grid gap-3 ${
          activeLanes.length === 1
            ? "grid-cols-1"
            : activeLanes.length === 2
            ? "grid-cols-1 md:grid-cols-2"
            : activeLanes.length === 3
            ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            : activeLanes.length === 4
            ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
            : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        }`}
      >
        {activeLanes.map((lane) => {
          const laneRequests = getRequestsByStatus(lane.id);

          return (
            <div
              key={lane.id}
              className={`rounded-xl border-2 ${lane.color} p-3 transition-all`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, lane.id)}
            >
              {/* Lane Header */}
              <div className="mb-3">
                <div className="flex items-center justify-between">
                  <h3 className={`font-semibold text-xs ${lane.color.split(" ")[2]}`}>
                    {lane.title}
                  </h3>
                  <span className="bg-white px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm">
                    {laneRequests.length}
                  </span>
                </div>
              </div>

              {/* Cards */}
              <div className="space-y-2 min-h-[400px]">
                {laneRequests.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 text-sm">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No requests</p>
                  </div>
                ) : (
                  laneRequests.map((request) => {
                    const counts = getItemCountsForRequest ? getItemCountsForRequest(request) : {};
                    const { totalQuantity: countedTotalQuantity, items: countedItems } = counts || {};

                    const requestItems = Array.isArray(request.items) ? request.items : [];
                    const nameIndex = buildNameIndexFromRequestItems(requestItems);

                    let itemsList = [];

                    if (Array.isArray(countedItems) && countedItems.length) {
                      itemsList = countedItems.map((it, idx) => {
                        const name = pickBestItemName(it, request, idx, nameIndex);
                        return { ...it, productName: name };
                      });
                    } else if (requestItems.length) {
                      itemsList = requestItems.map((it, idx) => {
                        const name = pickBestItemName(it, request, idx, nameIndex);
                        return { ...it, productName: name };
                      });
                    } else if (request.productId || request.productName || request.productCode) {
                      const name = pickBestItemName(
                        {
                          productName: request.productName,
                          productCode: request.productCode,
                          productId: request.productId,
                        },
                        request,
                        0,
                        nameIndex
                      );
                      itemsList = [
                        {
                          productId: request.productId || null,
                          productName: name,
                          productCode: request.productCode || null,
                          quantity: request.quantity || 1,
                        },
                      ];
                    } else {
                      itemsList = [{ productName: "Unknown Item", quantity: request.quantity || 1 }];
                    }

                    const totalQty =
                      countedTotalQuantity ||
                      itemsList.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0) ||
                      request.quantity ||
                      0;

                    const dateLabel = formatDeliveryDate(request);
                    const assignedLabel = request.assignedToName || "Unassigned";

                    const isInConvertedLane = request.status === "Converted to Order";
                    const draggable = isAdminOrOwner;

                    return (
                      <div
                        key={request.id}
                        draggable={draggable}
                        onDragStart={(e) => handleDragStart(e, request.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onSelectRequest && onSelectRequest(request)}
                        className={`bg-white rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-all ${
                          compactView ? "p-2" : "p-2.5"
                        } ${draggable ? "" : "select-text"}`}
                        title={!isAdminOrOwner ? "View only" : "Drag to move status"}
                      >
                        {compactView ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-xs text-gray-900 truncate">
                                {request.accountName || "No Account"}
                              </span>
                              {request.convertedToOrder && (
                                <ShoppingCart className="w-3 h-3 text-purple-600 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex items-center justify-between text-[10px] text-gray-500">
                              {dateLabel && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{dateLabel}</span>
                                </div>
                              )}
                              {totalQty > 0 && <span className="font-medium">Qty {totalQty}</span>}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Package className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className="font-semibold text-xs text-gray-900 truncate">
                                  {request.accountName || "No Account"}
                                </span>
                              </div>
                              {request.convertedToOrder && (
                                <ShoppingCart
                                  className="w-3 h-3 text-purple-600 flex-shrink-0"
                                  title="Converted to Order"
                                />
                              )}
                            </div>

                            <div className="mb-2 space-y-0.5">
                              {itemsList.length > 0 ? (
                                itemsList.map((item, idx) => {
                                  const itemName =
                                    (item.productName || item.productCode || item.sku || "")
                                      .toString()
                                      .trim() || `Item ${idx + 1}`;
                                  const itemQty = Number(item.quantity) || 1;

                                  return (
                                    <div
                                      key={idx}
                                      className="text-[10px] text-gray-700 truncate"
                                      title={`${itemQty}x ${itemName}`}
                                    >
                                      <span className="font-medium">{itemQty}x</span> {itemName}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="text-[10px] text-gray-700 truncate">
                                  {request.productName || "Sample Request"}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-gray-500 pt-1.5 border-t border-gray-100">
                              {dateLabel && (
                                <div className="inline-flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{dateLabel}</span>
                                </div>
                              )}
                              <div className="inline-flex items-center gap-1">
                                <User className="w-3 h-3" />
                                <span>{assignedLabel}</span>
                              </div>
                              {totalQty > 0 && (
                                <div className="inline-flex items-center gap-1 font-medium">
                                  <Package className="w-3 h-3" />
                                  <span>Qty {totalQty}</span>
                                </div>
                              )}
                            </div>

                            {isInConvertedLane && (
                              <div
                                className="mt-2 pt-2 border-t border-gray-100"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {request.convertedToOrder && request.orderNumber ? (
                                  <div className="flex items-center gap-1 text-[10px] text-purple-700 font-medium py-1 px-1.5 bg-purple-50 rounded">
                                    <ShoppingCart className="w-3 h-3" />
                                    <span>Order #{request.orderNumber}</span>
                                  </div>
                                ) : (
                                  isAdminOrOwner &&
                                  !request.convertedToOrder && (
                                    <>
                                      {convertOrderId === request.id ? (
                                        <div className="space-y-1.5">
                                          <input
                                            type="text"
                                            placeholder="Order #"
                                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                                            value={orderNumber}
                                            onChange={(e) => setOrderNumber(e.target.value)}
                                            autoFocus
                                          />
                                          <div className="flex gap-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleConvertToOrder(request.id);
                                              }}
                                              className="flex-1 text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium flex items-center justify-center gap-1 transition-colors"
                                            >
                                              <CheckCircle className="w-3 h-3" />
                                              Save
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setConvertOrderId(null);
                                                setOrderNumber("");
                                              }}
                                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setConvertOrderId(request.id);
                                          }}
                                          className="w-full text-xs px-2 py-1 bg-gray-50 text-gray-700 rounded border border-gray-200 hover:bg-gray-100 hover:border-gray-300 font-medium flex items-center justify-center gap-1 transition-colors"
                                        >
                                          <ShoppingCart className="w-3 h-3" />
                                          Convert
                                        </button>
                                      )}
                                    </>
                                  )
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SampleOrderKanban;
